/**
 * Stripe Commission Utilities
 * 
 * Fetches commission info from the backend and caches it.
 * This centralizes the commission constants to avoid duplication.
 */

import { fetchAPI } from './utils';
import { formatMoney } from './money';

// Default fallback values (must match backend)
const DEFAULT_PERCENT = 0.04;  // 4%
const DEFAULT_FIXED = 0.30;    // 0.30€

// "4% + 0,3€". Built here from the numbers: the backend's description has a
// decimal point ("0.30€").
const describeCommission = (percent, fixed) => `${(percent * 100).toFixed(0)}% + ${formatMoney(fixed)}€`;

// Cache for commission info
let cachedCommissionInfo = null;
let fetchPromise = null;

/**
 * Get commission info from backend (with caching)
 * @returns {Promise<{percent: number, fixed: number, description: string}>}
 */
export const getStripeCommissionInfo = () => {
    // Return cached data if available
    if (cachedCommissionInfo) {
        return Promise.resolve(cachedCommissionInfo);
    }

    // Return existing fetch promise if already in progress
    if (fetchPromise) {
        return fetchPromise;
    }

    // Fetch from backend
    fetchPromise = new Promise((resolve) => {
        fetchAPI('/stripe/commission-info', (data) => {
            if (data && data.success) {
                cachedCommissionInfo = {
                    percent: data.percent,
                    fixed: data.fixed,
                    description: describeCommission(data.percent, data.fixed)
                };
                resolve(cachedCommissionInfo);
            } else {
                // Use defaults on error
                cachedCommissionInfo = {
                    percent: DEFAULT_PERCENT,
                    fixed: DEFAULT_FIXED,
                    description: describeCommission(DEFAULT_PERCENT, DEFAULT_FIXED)
                };
                resolve(cachedCommissionInfo);
            }
            fetchPromise = null;
        }, false); // Don't use cache since we handle it ourselves
    });

    return fetchPromise;
};

/**
 * Get commission info synchronously (uses cached value or defaults)
 * Use this for immediate rendering, and call getStripeCommissionInfo() to refresh
 * @returns {{percent: number, fixed: number, description: string}}
 */
export const getStripeCommissionInfoSync = () => {
    return cachedCommissionInfo || {
        percent: DEFAULT_PERCENT,
        fixed: DEFAULT_FIXED,
        description: describeCommission(DEFAULT_PERCENT, DEFAULT_FIXED)
    };
};

/**
 * Calculate commission based on base amount
 * @param {number} baseAmount - The base amount in euros
 * @returns {number} The commission amount (rounded to 2 decimals)
 */
export const calculateCommission = (baseAmount) => {
    const { percent, fixed } = getStripeCommissionInfoSync();
    return Math.round((baseAmount * percent + fixed) * 100) / 100;
};

/**
 * Pre-fetch commission info (call on app init)
 */
export const prefetchCommissionInfo = () => {
    getStripeCommissionInfo().catch(() => {
        // Silently fail, will use defaults
    });
};
